/*
 * Copyright (c) 2021. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ContentPasteRounded from '@mui/icons-material/ContentPasteRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import OfflineBoltIcon from '@mui/icons-material/OfflineBolt';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import {
  Badge,
  Box,
  Button,
  type ButtonProps,
  Chip,
  type ChipProps,
  CircularProgress,
  Fab,
  type FabProps,
  IconButton,
  type IconButtonProps,
  ListItemIcon,
  ListItemText,
  MenuItem,
  type MenuItemProps,
  type Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import type { SxProps } from '@mui/system';
import {
  type FileExt,
  FLOW_FAILED_STATUSES,
  FLOW_RUNNING_STATUSES,
  FlowRunState,
} from '@poweralpha/common';
import { get } from 'lodash';
import { type ChangeEvent, useCallback, useMemo } from 'react';
import RunningIcon from '../icons/icon-sport-runner.svg?react';
import { useFileDownload } from './download/useFileDownload';

export interface StdButtonProps extends ButtonProps {
  label: string;
  onClick?: () => void;
  variant?: ButtonProps['variant'];
  color?: ButtonProps['color'];
}

export const StdButton: React.FC<StdButtonProps> = ({
  label,
  onClick,
  variant = 'outlined',
  color = 'primary',
  size = 'small',
  ...rest
}) => (
  <Button
    {...rest}
    variant={variant}
    color={color}
    size={size}
    onClick={onClick}
    data-testid={get(rest, 'data-testid') ?? label}
  >
    {label}
  </Button>
);

const circularProgressSx: SxProps<Theme> = {
  color: 'success.main',
  position: 'absolute',
  top: '50%',
  left: '50%',
  mt: '-12px',
  ml: '-12px',
};

export interface BadgedButtonProps extends ButtonProps {
  loading?: boolean;
  value?: number;
  badgeCount?: number;
  maxCount?: number;
  overrideCircularProgressSx?: SxProps<Theme>;
}

export const BadgedButton: React.FC<BadgedButtonProps> = ({
  loading = false,
  children,
  disabled,
  value,
  badgeCount,
  maxCount,
  fullWidth,
  overrideCircularProgressSx,
  ...rest
}) => {
  const testId =
    get(rest, 'data-testid') ??
    (Array.isArray(children) ? children[0]?.toString() : children?.toString());
  return (
    <Box
      sx={{
        display: fullWidth !== true ? 'inline-block' : 'block',
        position: 'relative',
      }}
    >
      <Badge
        badgeContent={badgeCount}
        color="primary"
        max={maxCount ?? 9999}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        invisible={!(badgeCount && badgeCount > 1)}
        sx={{ width: fullWidth ? '100%' : undefined }}
      >
        <Button
          {...rest}
          loading={loading && !value}
          disabled={disabled || loading}
          fullWidth={fullWidth}
          data-testid={testId}
        >
          {children}
        </Button>
      </Badge>
      {loading && value && (
        <CircularProgress
          size={24}
          variant="determinate"
          value={value}
          sx={overrideCircularProgressSx ?? circularProgressSx}
        />
      )}
    </Box>
  );
};

export type LoadingButtonProps = BadgedButtonProps;
export const LoadingButton: React.FC<LoadingButtonProps> = (props) => <BadgedButton {...props} />;

interface LoadingIconButtonProps extends IconButtonProps {
  loading: boolean;
  value?: number;
  size?: IconButtonProps['size'];
}

export const LoadingIconButton: React.FC<LoadingIconButtonProps> = ({
  loading,
  children,
  disabled,
  value,
  size = 'large',
  ...rest
}) => (
  <Box
    sx={{
      display: 'inline-block',
      position: 'relative',
    }}
  >
    <IconButton {...rest} disabled={disabled || loading} size={size}>
      {children}
    </IconButton>
    {loading && (
      <CircularProgress
        size={24}
        variant={value ? 'determinate' : 'indeterminate'}
        value={value}
        sx={circularProgressSx}
      />
    )}
  </Box>
);

interface LoadingChipProps extends ChipProps {
  label: React.ReactNode;
  loading: boolean;
  value?: number;
}

export const LoadingChip: React.FC<LoadingChipProps> = ({
  label,
  loading,
  disabled,
  value,
  size = 'medium',
  ...rest
}) => (
  <Box
    sx={{
      display: 'inline-block',
      position: 'relative',
    }}
  >
    <Chip {...rest} disabled={disabled || loading} size={size} label={label} />
    {loading && (
      <CircularProgress
        size={24}
        variant={value ? 'determinate' : 'indeterminate'}
        value={value}
        sx={circularProgressSx}
      />
    )}
  </Box>
);

interface LoadingMenuItemProps extends MenuItemProps {
  loading: boolean;
  icon?: React.ReactNode;
  text: string;
  value?: number;
}

const getTypographySxColor = (color: MenuItemProps['color']) => {
  if (!color || color === 'inherit') {
    return color;
  }
  return `${color}.main`;
};

export const LoadingMenuItem: React.FC<LoadingMenuItemProps> = ({
  loading,
  disabled,
  value,
  icon,
  text,
  color,
  ...rest
}) => {
  const typographyColor = getTypographySxColor(color);

  return (
    <MenuItem {...rest} disabled={disabled || loading}>
      {icon && <ListItemIcon>{icon}</ListItemIcon>}
      <ListItemText>
        <Typography sx={{ color: typographyColor }}>{text}</Typography>
      </ListItemText>
      {loading && (
        <CircularProgress
          size={24}
          variant={value ? 'determinate' : 'indeterminate'}
          value={value}
          sx={circularProgressSx}
        />
      )}
    </MenuItem>
  );
};

interface LoadingFabProps extends FabProps {
  loading: boolean;
  onClick: () => void;
  children: React.ReactNode;
  value?: number;
}

export const LoadingFab: React.FC<LoadingFabProps> = ({
  loading,
  onClick,
  value,
  children,
  ...rest
}) => (
  <Box
    sx={{
      position: 'relative',
    }}
  >
    <Fab
      {...rest}
      onClick={() => (loading ? null : onClick())}
      disableRipple={loading}
      sx={
        loading
          ? {
              bgcolor: 'grey',
              '&:hover': {
                bgcolor: 'grey',
              },
            }
          : null
      }
    >
      {children}
    </Fab>
    {loading && (
      <CircularProgress
        size={24}
        variant={value ? 'determinate' : 'indeterminate'}
        value={value}
        sx={{
          color: 'success.main',
          position: 'absolute',
          top: '50%',
          left: '50%',
          marginTop: '-12px',
          marginLeft: '-12px',
          zIndex: 1500,
        }}
      />
    )}
  </Box>
);

interface DownloadCsvIconButtonProps {
  path: string;
  baseFileName?: string;
  size?: IconButtonProps['size'];
  ext?: FileExt;
  disabled?: boolean;
  query?: Record<string, string>;
}

export const DownloadFileIconButton = ({
  path,
  baseFileName,
  size,
  query,
  ext = 'csv',
  disabled = false,
}: DownloadCsvIconButtonProps) => {
  const csvDownload = useFileDownload(path, { baseFileName, ext, query });

  return (
    <LoadingIconButton
      loading={csvDownload.isDownloading}
      onClick={csvDownload.startDownload}
      value={csvDownload.progress}
      size={size}
      disabled={disabled}
    >
      <SaveAltIcon />
    </LoadingIconButton>
  );
};

interface DownloadButtonProps extends Omit<DownloadCsvIconButtonProps, 'size'>, ButtonProps {
  label: string;
}

export const DownloadFileButton = ({
  path,
  baseFileName,
  size,
  query,
  ext = 'csv',
  disabled = false,
  label,
  ...rest
}: DownloadButtonProps) => {
  const csvDownload = useFileDownload(path, { baseFileName, ext, query });

  return (
    <LoadingButton
      {...rest}
      loading={csvDownload.isDownloading}
      onClick={csvDownload.startDownload}
      value={csvDownload.progress}
      size={size}
      disabled={disabled}
      startIcon={<SaveAltIcon />}
    >
      {label}
    </LoadingButton>
  );
};

export interface AddFileButtonProps extends Omit<ButtonProps<'label'>, 'color'> {
  id: string;
  fileAdded: boolean;
  handleChange: (file: FileList) => void;
  iconAsLabel?: boolean;
  addFileLabel?: string;
  fileAddedLabel?: string;
  required?: boolean;
  multiple?: boolean;
}

export function AddFileButton(props: AddFileButtonProps) {
  const {
    id,
    fileAdded,
    handleChange,
    iconAsLabel,
    addFileLabel = 'Add file',
    fileAddedLabel = 'File added',
    variant = 'contained',
    required = false,
    multiple = false,
    ...rest
  } = props;
  const color = useMemo(() => {
    if (required && !fileAdded) {
      return 'error';
    }
    return fileAdded ? 'success' : 'primary';
  }, [required, fileAdded]);

  const handleChangeClick = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { files } = e.currentTarget;
      if (files) {
        handleChange(files);
      }

      e.target.value = '';
    },
    [handleChange],
  );

  const children = useMemo(() => {
    if (iconAsLabel) {
      return <AttachFileIcon />;
    }
    return fileAdded ? fileAddedLabel : addFileLabel;
  }, [iconAsLabel, fileAdded, fileAddedLabel, addFileLabel]);

  return (
    <Button
      {...rest}
      component="label"
      variant={variant}
      disableElevation
      color={color}
      startIcon={!iconAsLabel ? <AttachFileIcon /> : undefined}
    >
      {children}
      <input
        type="file"
        multiple={multiple}
        id={id}
        data-testid={id}
        name={id}
        hidden
        onChange={handleChangeClick}
      />
    </Button>
  );
}

export const SimIconButton = ({
  state,
  handleSimulate,
  disabled,
}: {
  state: FlowRunState | undefined;
  handleSimulate: () => void;
  disabled: boolean;
}) => {
  if (state && FLOW_RUNNING_STATUSES.includes(state)) {
    return (
      <Tooltip title="Running">
        <RunningIcon height={27} data-testid="running-icon" />
      </Tooltip>
    );
  }

  return (
    <LoadingIconButton
      color="secondary"
      onClick={handleSimulate}
      disabled={disabled}
      loading={false}
    >
      <Badge
        color={FLOW_FAILED_STATUSES.includes(state ?? FlowRunState.FAILED) ? 'error' : 'success'}
        variant="dot"
        overlap="circular"
        invisible={!state}
      >
        <OfflineBoltIcon />
      </Badge>
    </LoadingIconButton>
  );
};

interface CopyButtonProps {
  value: string;
}

export const CopyButton = ({ value }: Readonly<CopyButtonProps>) => {
  const onClick = useCallback(() => navigator.clipboard.writeText(value), [value]);
  return (
    <Tooltip title="Copy">
      <IconButton size="small" onClick={onClick} sx={{ width: 28, height: 28 }}>
        <ContentPasteRounded sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );
};

export interface ExpandButtonProps extends IconButtonProps {
  collapsed: boolean;
  reverse?: boolean;
  iconSx?: SxProps;
}

export const ExpandIconButton = ({ collapsed, iconSx, reverse, ...props }: ExpandButtonProps) => {
  return (
    <IconButton
      {...props}
      sx={{
        ...props.sx,
        transition: 'transform 0.2s ease-in-out',
        transform: collapsed ? `rotate(${reverse ? '' : '-'}90deg)` : 'rotate(0deg)',
      }}
    >
      <ExpandMoreRounded sx={iconSx} />
    </IconButton>
  );
};

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Menu } from '@mui/material';
import { type MouseEvent, useState } from 'react';
import { overrideSx } from '../styles';

const actionButtonSx = {
  height: 32,
  minWidth: 120,
} as const;

interface MultiDownloadButtonProps {
  menuItems: Array<LoadingMenuItemProps & { tooltip?: string | null }>;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

export function MultiDownloadButton({
  menuItems,
  disabled,
  sx,
}: Readonly<MultiDownloadButtonProps>) {
  const [downloadMenuAnchorEl, setDownloadMenuAnchorEl] = useState<HTMLElement | null>(null);

  const isDownloadMenuOpen = Boolean(downloadMenuAnchorEl);

  const handleOpenDownloadMenu = useCallback((event: MouseEvent<HTMLElement>) => {
    setDownloadMenuAnchorEl(event.currentTarget);
  }, []);

  const handleCloseDownloadMenu = useCallback(() => {
    setDownloadMenuAnchorEl(null);
  }, []);

  return (
    <>
      <LoadingButton
        variant="outlined"
        size="small"
        onClick={handleOpenDownloadMenu}
        loading={menuItems.some((item) => item.loading)}
        startIcon={<SaveAltIcon sx={{ height: 18, width: 18 }} />}
        endIcon={<ArrowDropDownIcon />}
        aria-haspopup="menu"
        aria-expanded={isDownloadMenuOpen ? 'true' : undefined}
        aria-controls={isDownloadMenuOpen ? `multi-download-menu` : undefined}
        disabled={disabled || menuItems.every((item) => item.disabled)}
        sx={(t) => overrideSx(actionButtonSx, t, sx)}
      >
        Download
      </LoadingButton>
      <Menu
        id="multi-download-menu"
        anchorEl={downloadMenuAnchorEl}
        open={isDownloadMenuOpen}
        onClose={handleCloseDownloadMenu}
      >
        {menuItems.map((item) => {
          const menuItem = (
            <LoadingMenuItem
              {...item}
              key={item.key}
              onClick={(e) => {
                item.onClick?.(e);
                handleCloseDownloadMenu();
              }}
              icon={<SaveAltIcon fontSize="small" />}
              text={item.text}
            />
          );

          if (!item.tooltip) {
            return menuItem;
          }

          return (
            <Tooltip key={item.key} title={item.tooltip} placement="left">
              <span>{menuItem}</span>
            </Tooltip>
          );
        })}
      </Menu>
    </>
  );
}
